import React, { useState, useEffect } from "react";
import { Card, Button, Form, Alert, Spinner, Row, Col, Tabs, Tab } from "react-bootstrap";
import { authFetch } from "../utils/authFetch";
import * as XLSX from "xlsx";

export default function ImportStoc() {
  const [activeTab, setActiveTab] = useState("stoc"); // 'stoc' or 'coduri'
  const [gestiuni, setGestiuni] = useState([]);
  const [selectedGestiuneId, setSelectedGestiuneId] = useState("");
  
  const [grupeProduse, setGrupeProduse] = useState([]);
  const [selectedGrupaId, setSelectedGrupaId] = useState("");
  const [selectedTipGrupa, setSelectedTipGrupa] = useState(2); // default 2
  const [selectedContIntrareId, setSelectedContIntrareId] = useState("");

  const [accounts, setAccounts] = useState([]);

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const resG = await authFetch("/bxgestiune?limit=100");
      if (resG && resG.items) setGestiuni(resG.items);

      const resGP = await authFetch("/grupa_produse?limit=100");
      if (resGP && resGP.items) setGrupeProduse(resGP.items);

      const resAcc = await authFetch("/accounts?limit=1000&sort_by=code");
      if (resAcc && resAcc.items) {
        setAccounts(resAcc.items);
      }
    } catch (err) {
      console.error("Eroare la incarcarea nomenclatoarelor:", err);
    }
  };

  // When selected product group changes, auto-populate tip_grupa and contintrare_id if available
  const handleGrupaChange = (e) => {
    const gid = e.target.value;
    setSelectedGrupaId(gid);
    const gp = grupeProduse.find(x => String(x.id) === String(gid));
    if (gp) {
      setSelectedTipGrupa(gp.tip_grupa || 2);
      if (gp.contintrare_id) {
        setSelectedContIntrareId(gp.contintrare_id);
      }
    }
  };

  // Set default values on first load
  useEffect(() => {
    if (gestiuni.length > 0 && !selectedGestiuneId) {
      setSelectedGestiuneId(gestiuni[0].id);
    }
  }, [gestiuni]);

  useEffect(() => {
    if (grupeProduse.length > 0 && !selectedGrupaId) {
      const first = grupeProduse[0];
      setSelectedGrupaId(first.id);
      setSelectedTipGrupa(first.tip_grupa || 2);
      setSelectedContIntrareId(first.contintrare_id || "");
    }
  }, [grupeProduse]);

  // If no contintrare_id is matched from group, select first account starting with 371
  useEffect(() => {
    if (!selectedContIntrareId && accounts.length > 0) {
      const defaultAcc = accounts.find(acc => acc.code.startsWith("371")) || accounts[0];
      if (defaultAcc) setSelectedContIntrareId(defaultAcc.id);
    }
  }, [accounts, selectedContIntrareId]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError("");
      setSuccess("");
    }
  };

  const downloadTemplate = () => {
    let headers, filename;
    if (activeTab === "stoc") {
      headers = [
        ["COD_PRODUS", "DENUMIRE_PRODUS", "UM", "CANTITATE", "PRET_ACHIZITIE", "PRET_VANZARE"],
        ["PROD-001", "Produs Exemplu 1", "BUC", 10, 15.50, 24.99],
        ["PROD-002", "Produs Exemplu 2", "BUC", 50, 4.20, 6.99]
      ];
      filename = "model_import_stoc_initial.xlsx";
    } else {
      headers = [
        ["COD_PRODUS", "DENUMIRE_PRODUS", "UM", "COD_BARE", "COD_VAMAL_NC8"],
        ["PROD-001", "Produs Exemplu 1", "BUC", "5941234567890", "12345678"],
        ["PROD-002", "Produs Exemplu 2", "BUC", "5940987654321", "87654321"]
      ];
      filename = "model_import_coduri_produse.xlsx";
    }

    const ws = XLSX.utils.aoa_to_sheet(headers);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, filename);
  };

  const handleUpload = () => {
    if (!file) {
      setError("Te rog selecteaza un fisier Excel mai intai.");
      return;
    }

    if (activeTab === "stoc") {
      if (!selectedGestiuneId || !selectedGrupaId || !selectedContIntrareId) {
        setError("Te rog completeaza gestiunea, grupa de produse si contul de stoc.");
        return;
      }
    }

    setLoading(true);
    setError("");
    setSuccess("");

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const bstr = e.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const rawData = XLSX.utils.sheet_to_json(ws);

        const cleanedData = rawData.map(row => {
          const newRow = {};
          Object.keys(row).forEach(key => {
            const normKey = key.toLowerCase().trim().replace(/[\s\-_.]+/g, "_");
            newRow[normKey] = row[key];
          });
          return newRow;
        }).filter(row => row.bxcod || row.cod || row.cod_produs || row.articol);

        if (cleanedData.length === 0) {
          throw new Error("Fisierul Excel nu contine date valide. Asigura-te ca exista coloana COD_PRODUS (sau BXCOD).");
        }

        const endpoint = activeTab === "stoc" ? "/imports/stoc-initial" : "/imports/coduri-produse";
        const bodyObj = activeTab === "stoc" ? {
          data: cleanedData,
          gestiune_id: selectedGestiuneId,
          grupa_produse_id: selectedGrupaId,
          tip_grupa_produse: selectedTipGrupa,
          contintrare_id: selectedContIntrareId
        } : {
          data: cleanedData
        };

        const res = await authFetch(endpoint, {
          method: "POST",
          body: JSON.stringify(bodyObj)
        });

        if (res.result === "pass") {
          setSuccess(res.message || "Importul a fost realizat cu succes!");
          setFile(null);
          setFileName("");
        } else {
          setError(res.message || "Importul a esuat.");
        }
      } catch (err) {
        setError(err.message || "Eroare la procesarea fisierului Excel.");
      } finally {
        setLoading(false);
      }
    };

    reader.readAsBinaryString(file);
  };

  return (
    <Card className="shadow-lg border-0 rounded-4 overflow-hidden mb-4 bg-white">
      <div className="bg-gradient bg-primary p-4 text-white">
        <h3 className="mb-1 fw-bold">Import Stoc / Nomenclator Produse</h3>
        <p className="mb-0 text-white-50 small">Preluarea stocului inițial sau importarea nomenclatorului de articole.</p>
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => {
          setActiveTab(k);
          setError("");
          setSuccess("");
          setFile(null);
          setFileName("");
        }}
        className="bg-light border-bottom px-4 pt-2"
      >
        <Tab eventKey="stoc" title="Import Stoc Initial" />
        <Tab eventKey="coduri" title="Import Coduri Produse" />
      </Tabs>

      <Card.Body className="p-4">
        {error && <Alert variant="danger" className="rounded-3">{error}</Alert>}
        {success && <Alert variant="success" className="rounded-3">{success}</Alert>}

        {activeTab === "stoc" && (
          <Form className="mb-4">
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold text-dark mb-2">Gestiune</Form.Label>
                  <Form.Select
                    id="select-gestiune"
                    value={selectedGestiuneId}
                    onChange={(e) => setSelectedGestiuneId(e.target.value)}
                    className="rounded-3"
                  >
                    {gestiuni.map(g => (
                      <option key={g.id} value={g.id}>{g.bxnume || g.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold text-dark mb-2">Grupa de produse</Form.Label>
                  <Form.Select
                    id="select-grupa"
                    value={selectedGrupaId}
                    onChange={handleGrupaChange}
                    className="rounded-3"
                  >
                    {grupeProduse.map(gp => (
                      <option key={gp.id} value={gp.id}>{gp.denumire || gp.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold text-dark mb-2">Cont stoc asociat</Form.Label>
                  <Form.Select
                    id="select-cont-stoc"
                    value={selectedContIntrareId}
                    onChange={(e) => setSelectedContIntrareId(e.target.value)}
                    className="rounded-3"
                  >
                    {accounts.map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.code} - {acc.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        )}

        <div className="mb-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h5 className="fw-bold text-dark mb-1">Model Excel</h5>
            <p className="text-muted small mb-0">Descarcă modelul corespunzător secțiunii curente.</p>
          </div>
          <Button variant="outline-primary" onClick={downloadTemplate} className="rounded-pill px-4">
            Descarcă model Excel
          </Button>
        </div>

        <hr className="my-4 text-black-50" />

        <Form.Group className="mb-4">
          <Form.Label className="fw-bold text-dark mb-2">Selectare fișier import</Form.Label>
          <div className="border border-2 border-dashed border-primary rounded-3 p-4 text-center cursor-pointer position-relative">
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileChange}
              className="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer"
            />
            <div className="py-2">
              <i className="bi bi-file-earmark-excel text-primary display-4 mb-2"></i>
              <p className="fw-semibold text-secondary mb-1">
                {fileName || "Trage fișierul aici sau fă click pentru a-l selecta"}
              </p>
              <p className="text-muted small mb-0">Sunt acceptate doar fișiere Excel (.xlsx, .xls)</p>
            </div>
          </div>
        </Form.Group>

        <div className="d-flex justify-content-end gap-2">
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={loading || !file || (activeTab === "stoc" && (!selectedGestiuneId || !selectedGrupaId || !selectedContIntrareId))}
            className="rounded-pill px-4 py-2 fw-semibold d-flex align-items-center"
          >
            {loading && <Spinner size="sm" animation="border" className="me-2" />}
            {loading ? "Se importă..." : "Efectuează Import"}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}
